/**
 * SSE čtečka pro chat (spec 07 FR-1).
 *
 * Nepoužívá EventSource, protože ten umí jen GET a neposílá tělo — chat potřebuje
 * POST se zprávou a kontextem stránky. Čte se tedy `fetch` + ReadableStream a
 * rámce `data: {...}\n\n` se parsují ručně.
 */

import { apiUrl, ApiError } from './client';

/**
 * Události chat streamu. Backend je posílá jako `data: {...}` rámce; přesný
 * výčet typů není v OpenAPI (endpoint je deklarovaný jako volné JSON),
 * takže tenhle union je jediné místo, kde se drží ručně.
 */
export type ChatStreamEvent =
  | { type: 'text'; delta: string }
  | { type: 'tool_call'; name: string; label_cs?: string; detail?: string }
  | { type: 'tool_result'; name: string; status: 'done' | 'failed'; detail?: string }
  | { type: 'done'; message_id?: number; model?: string; cost_usd?: number }
  | { type: 'error'; message: string };

export interface StreamHandlers {
  onEvent: (event: ChatStreamEvent) => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
}

/**
 * Tvar jedné otázky. Odpovídá `ChatAskIn`, ale `page_context` je tu volný objekt
 * — v OpenAPI je to `type: object` bez vlastností, které openapi-typescript
 * vyrenderuje jako neplnitelný `Record<string, never>`.
 */
export interface ChatAskPayload {
  question: string;
  model?: string | null;
  page_context?: Record<string, unknown>;
}

export async function streamChat(
  sessionId: number,
  payload: ChatAskPayload,
  handlers: StreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(apiUrl(`/ai/chat/sessions/${sessionId}/messages`), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(payload),
      signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') return;
    handlers.onError?.(err as Error);
    return;
  }

  if (res.status === 403) {
    // Chat je globálně vypnutý (ai_settings.chat_enabled = false).
    handlers.onError?.(
      new ApiError(403, 'Chat je administrátorem vypnutý. Dávkové AI joby běží dál.'),
    );
    return;
  }
  if (!res.ok || !res.body) {
    handlers.onError?.(new ApiError(res.status, `Chat stream selhal (HTTP ${res.status}).`));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Rámce oddělené prázdným řádkem.
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        handleFrame(frame, handlers);
        boundary = buffer.indexOf('\n\n');
      }
    }
  } catch (err) {
    if ((err as Error).name !== 'AbortError') {
      handlers.onError?.(err as Error);
    }
  } finally {
    handlers.onClose?.();
  }
}

function handleFrame(frame: string, handlers: StreamHandlers) {
  const dataLines = frame
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trimStart());

  if (dataLines.length === 0) return;
  const raw = dataLines.join('\n');
  if (raw === '[DONE]') return;

  try {
    handlers.onEvent(JSON.parse(raw) as ChatStreamEvent);
  } catch {
    // Nevalidní rámec radši ignorujeme, než abychom shodili celý stream.
    console.warn('Nevalidní SSE rámec z chatu:', raw.slice(0, 200));
  }
}
